import { AppointmentListingModel, HospitalModel } from "@common/models";
import { AppointmentFlowActionOperations, AppointmentStatus } from "@common/models/Enums";
import { IAppointmentCheckInRequest, IVisitCheckoutRequest } from "@common/models/requests";
import { IAppointmentStatusRequest } from "@common/models/requests/AppointmentStatusRequest";
import { HospitalService } from "@common/services";
import { NotificationStoreInstance } from "@common/stores";
import { ICheckInModel } from "@models/forms/ICheckInModel";
import { isUndefined } from "lodash";
import { action, flow, makeObservable, observable } from "mobx";

export class AppointmentStore {
    @observable currentHospital: HospitalModel;
    @observable selectedOperation?: AppointmentFlowActionOperations;
    @observable selectedOperationFields: ICheckInModel | undefined;
    @observable shouldNavigate: boolean = true;
    @observable currentAppointment: AppointmentListingModel | undefined;

    constructor(init: { currentHospital: HospitalModel }) {
        this.currentHospital = init.currentHospital;
        makeObservable(this, undefined, { autoBind: true });
    }

    /**
    * Triggered when one of the action buttons are clicked on an event modal or flow card
    * Stores the selected operation
    * @param operation current operation
    */
    @action.bound setCurrentAction(this: AppointmentStore, operation: AppointmentFlowActionOperations, shouldNavigate?: boolean) {
        this.selectedOperation = operation;
        this.shouldNavigate = !isUndefined(shouldNavigate) ? shouldNavigate : true;
    }

    /**
    * Triggered when the Yes button is clicked in the confirm modal
    * Takes the currently selected appointment and operation and triggers the request
    */
    triggerCurrentAction = flow(function* (this: AppointmentStore, appointment: AppointmentListingModel) {
        return yield this.triggerRequest(appointment, this.selectedOperation).then((appointment: AppointmentListingModel) => {
            return appointment;
        });
    });

    /**
   * Called when one of the current actions has been triggered
   * Maps the current appointment status to its correlating service request
   * @param appointment current appointment
   */
    triggerRequest = flow(function* (this: AppointmentStore, appointment: AppointmentListingModel, operation?: AppointmentFlowActionOperations) {
        this.currentAppointment = appointment;
        switch (appointment.appointmentStatusId) {
            case AppointmentStatus.Booked:
            case AppointmentStatus.Confirmed:
            case AppointmentStatus.Showed:
                if (operation == AppointmentFlowActionOperations.CheckIn) {
                    const checkInRequest = appointment.toCheckInRequest(this.selectedOperationFields!);
                    const response = yield this.checkInAppointment(checkInRequest);

                    if (!response) {
                        throw new Error('Unable to set Appointment as Checked In')
                    }
                    break;
                }
                else if (operation === AppointmentFlowActionOperations.StartVisit) {
                    const checkInRequest = appointment.toCheckInRequest(this.selectedOperationFields!, true);
                    const response = yield this.checkInAppointment(checkInRequest);

                    if (!response) {
                        throw new Error('Unable to set Appointment as In Progress')
                    }
                    break;
                }
                else if (operation == AppointmentFlowActionOperations.NoShow) {
                    const statusRequest = appointment.toStatusRequest();
                    yield this.setNoShowAppointment(statusRequest);
                    break;
                }
                else if (operation == AppointmentFlowActionOperations.Book) {
                    const statusRequest = appointment.toStatusRequest();
                    yield this.setBookedAppointment(statusRequest);
                    break;
                }
                else if (operation == AppointmentFlowActionOperations.Confirm) {
                    const statusRequest = appointment.toStatusRequest();
                    yield this.setConfirmedAppointment(statusRequest);
                    break;
                }
                else if (operation == AppointmentFlowActionOperations.Cancel) {
                    const statusRequest = appointment.toStatusRequest();
                    yield this.setCancelledAppointment(statusRequest);
                    break;
                }

                throw new Error('Not Implemented')
            case AppointmentStatus.ClinicCancelled:
                throw new Error('Not Implemented')
            case AppointmentStatus.NoShow:
                if (operation == AppointmentFlowActionOperations.CheckIn) {
                    const checkInRequest = appointment.toCheckInRequest(this.selectedOperationFields!);
                    const response = yield this.checkInAppointment(checkInRequest);

                    if (!response) {
                        throw new Error('Unable to set Appointment as Checked In')
                    }
                    break;
                }

                throw new Error('Not Implemented')
            case AppointmentStatus.In_Progress:
                if (operation == AppointmentFlowActionOperations.Admit) {
                    const statusRequest = appointment.toStatusRequest();
                    const response = yield this.setAdmittedAppointment(statusRequest);

                    if (!response) {
                        throw new Error('Unable to set Appointment as Admitted')
                    }
                    break;
                }

                throw new Error('Not Implemented')
            case AppointmentStatus.Check_In:
                if (operation == AppointmentFlowActionOperations.StartVisit) {
                    const statusRequest = appointment.toStatusRequest();
                    const response = yield this.setInProgressAppointment(statusRequest);

                    if (!response) {
                        throw new Error('Unable to set Appointment as In Progress')
                    }
                    break;
                }
                else if (operation == AppointmentFlowActionOperations.Admit) {
                    const statusRequest = appointment.toStatusRequest();
                    const response = yield this.setAdmittedAppointment(statusRequest);

                    if (!response) {
                        throw new Error('Unable to set Appointment as Admitted')
                    }
                    break;
                }

                throw new Error('Not Implemented')
            case AppointmentStatus.Admitted:
                throw new Error('Not Implemented')
            case AppointmentStatus.Finalized:
                if (operation == AppointmentFlowActionOperations.CheckOut) {
                    yield this.setPaidAppointment(appointment.visitId!);
                    return appointment; // **Placeholder until checkout functionality is added**
                }

                throw new Error('Not Implemented')
            case AppointmentStatus.Paid:
                throw new Error('Not Implemented')
            case AppointmentStatus.Cancelled:
                if (operation == AppointmentFlowActionOperations.Cancel) {
                    const statusRequest = appointment.toStatusRequest();
                    yield this.setCancelledAppointment(statusRequest);
                    break;
                }
                else if (operation == AppointmentFlowActionOperations.Book) {
                    const statusRequest = appointment.toStatusRequest();
                    yield this.setBookedAppointment(statusRequest);
                    break;
                }
                else if (operation == AppointmentFlowActionOperations.Confirm) {
                    const statusRequest = appointment.toStatusRequest();
                    yield this.setConfirmedAppointment(statusRequest);
                    break;
                }

                throw new Error('Not Implemented')
            default:
                throw new Error('Error: Unexpected Appointment Status');
        }

        return this.currentAppointment;
    });

    /**
    * Triggered when the current action requires additional field changes
    * Stores the selected operation object - ICheckInModel | IxxxModel | undefined
    * @param operation current operation
    */
    @action.bound setSelectedOperationFields(selectedOperationFields: ICheckInModel | undefined) {
        this.selectedOperationFields = selectedOperationFields
    }

    /**
    * Triggers the checkInAppointment hospital service method
    * @param appointment check in request object
    */
    checkInAppointment = flow(function* (this: AppointmentStore, appointment: IAppointmentCheckInRequest) {
        let success = false;
        this.currentAppointment = yield HospitalService.checkInAppointment(
            this.currentHospital!.hospitalId,
            appointment)
            .then((data) => {
                NotificationStoreInstance.onSuccess("Appointment Checked In");
                success = true;
                return data;
            })
            .catch(NotificationStoreInstance.onError("Failed check in appointment.", this.currentAppointment));
        return success;
    });

    /**
     * Triggers the SetNoShow hospital service method
     * @param appointment current appointment
     */
    setNoShowAppointment = flow(function* (this: AppointmentStore, appointment: IAppointmentStatusRequest) {
        this.currentAppointment = yield HospitalService.setNoShowAppointment(
            this.currentHospital!.hospitalId,
            appointment)
            .then(NotificationStoreInstance.onSuccess("Appointment Marked as No Show"))
            .catch(NotificationStoreInstance.onError("Failed to mark appointment as No Show.", this.currentAppointment));
    });

    /**
    * Triggers the InProgress hospital service method
    * @param appointment current appointment
    */
    setInProgressAppointment = flow(function* (this: AppointmentStore, appointment: IAppointmentStatusRequest) {
        let success = false;
        this.currentAppointment = yield HospitalService.setInProgressAppointment(
            this.currentHospital!.hospitalId,
            appointment)
            .then((data) => {
                NotificationStoreInstance.onSuccess("Appointment Marked as In Progress");
                success = true;
                return data;
            })
            .catch(NotificationStoreInstance.onError("Failed to mark appointment as In Progress.", this.currentAppointment));
        return success;
    });

    /**
    * Triggers the setAdmittedAppointment hospital service method
    * @param appointment current appointment
    */
    setAdmittedAppointment = flow(function* (this: AppointmentStore, appointment: IAppointmentStatusRequest) {
        let success = false;
        this.currentAppointment = yield HospitalService.setAdmittedAppointment(
            this.currentHospital!.hospitalId,
            appointment
        )
            .then((data) => {
                NotificationStoreInstance.success("Appointment Marked as Admitted")
                success = true;
                return data
            })
            .catch(NotificationStoreInstance.onError("Failed to mark appointment as Admitted.", this.currentAppointment));

        return success;
    });

    /**
     * Triggers the SetBooked hospital service method
     * @param appointment current appointment
     */
    setBookedAppointment = flow(function* (this: AppointmentStore, appointment: IAppointmentStatusRequest) {
        this.currentAppointment = yield HospitalService.setBookedAppointment(
            this.currentHospital!.hospitalId,
            appointment)
            .then(NotificationStoreInstance.onSuccess("Appointment Marked as booked"))
            .catch(NotificationStoreInstance.onError("Failed to mark appointment as Booked.", this.currentAppointment));
    });

    /**
     * Triggers the SetConfirmed hospital service method
     * @param appointment current appointment
     */
    setConfirmedAppointment = flow(function* (this: AppointmentStore, appointment: IAppointmentStatusRequest) {
        this.currentAppointment = yield HospitalService.setConfirmedAppointment(
            this.currentHospital!.hospitalId,
            appointment)
            .then(NotificationStoreInstance.onSuccess("Appointment Marked as confirmed"))
            .catch(NotificationStoreInstance.onError("Failed to mark appointment as Confirmed.", this.currentAppointment));
    });

    /**
     * Triggers the SetCancelled hospital service method
     * @param appointment current appointment
     */
    setCancelledAppointment = flow(function* (this: AppointmentStore, appointment: IAppointmentStatusRequest) {
        this.currentAppointment = yield HospitalService.setCancelledAppointment(
            this.currentHospital!.hospitalId,
            appointment)
            .then(NotificationStoreInstance.onSuccess("Appointment Marked as Cancelled"))
            .catch(NotificationStoreInstance.onError("Failed to mark appointment as Cancelled.", this.currentAppointment));
    });

    /**
    * Triggers the setFinalizedAppointment hospital service method
    * @param appointment current appointment
    */
    setFinalizedAppointment = flow(function* (this: AppointmentStore, appointment: IAppointmentStatusRequest) {
        let success = false;
        this.currentAppointment = yield HospitalService.setFinalizedAppointment(
            this.currentHospital!.hospitalId,
            appointment
        )
            .then((data) => {
                NotificationStoreInstance.success("Appointment Marked as Finalized")
                success = true;
                return data
            })
            .catch(NotificationStoreInstance.onError("Failed to mark appointment as Finalized.", this.currentAppointment));

        return success;
    });

    setCompletedAppointment = flow(function* (this: AppointmentStore, appointment: IAppointmentStatusRequest) {
        let success = false;
        this.currentAppointment = yield HospitalService.setCompletedAppointment(
            this.currentHospital!.hospitalId,
            appointment
        )
            .then((data) => {
                NotificationStoreInstance.success("Appointment Marked as Complete")
                success = true;
                return data
            })
            .catch(NotificationStoreInstance.onError("Failed to mark appointment as Complete.", this.currentAppointment));

        return success;
    });

    setAbortedAppointment = flow(function* (this: AppointmentStore, request: IVisitCheckoutRequest) {
        let success = false;
        this.currentAppointment = yield HospitalService.setAbortedAppointment(
            this.currentHospital!.hospitalId,
            request
        )
            .then((data) => {
                NotificationStoreInstance.success("Appointment Marked as Aborted")
                success = true;
                return data
            })
            .catch(NotificationStoreInstance.onError("Failed to mark appointment as Aborted.", this.currentAppointment));

        return success;
    });

    /**
    * Triggers the setPaidAppointment hospital service method
    * @param appointment current appointment
    */
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    setPaidAppointment = flow(function* (this: AppointmentStore, visitId: number) {

        //**Placeholder until checkout functionality is added**
        // const updatedAppointment = yield HospitalService.setPaidAppointment(
        //     this.currentHospital!.hospitalId,
        //     visitId,
        //     { showSuccess: true, showError: true });

        return yield;
    });

    reopenVisit = flow(function* (this: AppointmentStore, appointment: IAppointmentStatusRequest) {
        return yield HospitalService.reopenVisit(this.currentHospital!.hospitalId, appointment)
            .then(NotificationStoreInstance.onSuccess("Visit successfully reopened"))
            .catch(NotificationStoreInstance.onError("Failed to reopen Visit", undefined));
    });
}
